import { getAuth } from 'firebase/auth';
import EnvironmentManager, { buildUrl } from '../config/EnvironmentManager';
import { cacheService } from './CacheService';
import { getUserTimeZone } from '../utils/timezone';

const baseUrl = EnvironmentManager.baseUrl;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Validates selected profile image file.
 * Allowed formats: JPG, JPEG, PNG, WEBP.
 * Maximum file size: 5 MB.
 */
export const validateProfileImage = (file) => {
  if (!file) {
    return { valid: false, error: 'Please select an image file.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Profile image must not exceed 5 MB.' };
  }

  const fileName = file.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
    return { valid: false, error: 'Only JPG, JPEG, PNG, and WEBP formats are allowed.' };
  }

  const mimeType = file.type?.toLowerCase();
  if (mimeType && !ALLOWED_MIME_TYPES.includes(mimeType)) {
    return { valid: false, error: 'Unsupported image type. Only JPG, PNG, and WEBP are allowed.' };
  }

  return { valid: true };
};

const getAuthHeaders = async (isMultipart = false) => {
  const auth = getAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  const headers = {
    'TokenType': 'developer',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

export const userProfileImageService = {
  validateImage: validateProfileImage,

  /**
   * Fetch profile image metadata for a user.
   * GET /mv-profile/user-profile-image/{userId}?zoneId={zoneId}
   */
  getProfileImage: async (userId, customZoneId = null) => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    try {
      const headers = await getAuthHeaders();
      const rawZone = customZoneId || getUserTimeZone();
      const zoneId = rawZone ? rawZone.replace('/', '-') : 'UTC';

      const url = `${buildUrl(EnvironmentManager.endpoints.getProfileImage, userId)}?zoneId=${encodeURIComponent(zoneId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (response.status === 404 || response.status === 204) {
        return { success: true, data: null };
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(errorText || `Failed to fetch profile image (Status: ${response.status})`);
      }

      const text = await response.text();
      if (!text || !text.trim() || text.trim() === 'null') {
        return { success: true, data: null };
      }

      const data = JSON.parse(text);
      return { success: true, data };
    } catch (error) {
      console.error('Error in getProfileImage:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Fetch image file from backend proxy and return a browser-compatible object URL.
   * Handles GCS object paths returned by the backend (e.g. profile-images/...).
   */
  getImageBlobUrl: async (profileImageUrl) => {
    if (!profileImageUrl) return { success: false, url: null };

    // If it's already a full URL or blob URL, return it directly
    if (profileImageUrl.startsWith('blob:') || profileImageUrl.startsWith('data:') || profileImageUrl.startsWith('http://') || profileImageUrl.startsWith('https://')) {
      return { success: true, url: profileImageUrl };
    }

    const cacheKey = `profile_image_blob_${profileImageUrl}`;
    const cachedBlob = cacheService.get(cacheKey);
    if (cachedBlob) {
      const url = window.URL.createObjectURL(cachedBlob);
      return { success: true, url };
    }

    try {
      const headers = await getAuthHeaders();
      const downloadEndpoint = EnvironmentManager.endpoints.downloadFile;
      const backendUrl = `${baseUrl}${downloadEndpoint}?fileUrl=${encodeURIComponent(profileImageUrl)}`;

      const response = await fetch(backendUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to download profile image (Status: ${response.status})`);
      }

      const blob = await response.blob();
      cacheService.set(cacheKey, blob, 60 * 60 * 1000); // 1 hour cache
      const objectUrl = window.URL.createObjectURL(blob);
      return { success: true, url: objectUrl };
    } catch (error) {
      console.error('Error fetching image blob URL:', error);
      return { success: false, error: error.message, url: null };
    }
  },

  /**
   * Upload a new profile image.
   * POST /mv-profile/user-profile-image/upload/{userId}
   * Request: multipart/form-data with key 'file'
   */
  uploadProfileImage: async (userId, file) => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const validation = validateProfileImage(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    try {
      const headers = await getAuthHeaders(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = `${buildUrl(EnvironmentManager.endpoints.uploadProfileImage, userId)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || `Failed to upload profile image (Status: ${response.status})`);
      }

      return { success: true, data: responseText };
    } catch (error) {
      console.error('Error in uploadProfileImage:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update an existing profile image.
   * POST /mv-profile/user-profile-image/update/{userId}
   * Request: multipart/form-data with key 'file'
   */
  updateProfileImage: async (userId, file) => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const validation = validateProfileImage(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    try {
      const headers = await getAuthHeaders(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = `${buildUrl(EnvironmentManager.endpoints.updateProfileImage, userId)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || `Failed to update profile image (Status: ${response.status})`);
      }

      return { success: true, data: responseText };
    } catch (error) {
      console.error('Error in updateProfileImage:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete an existing profile image.
   * DELETE /mv-profile/user-profile-image/{userId}
   */
  deleteProfileImage: async (userId) => {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    try {
      const headers = await getAuthHeaders();
      const url = `${buildUrl(EnvironmentManager.endpoints.deleteProfileImage, userId)}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || `Failed to delete profile image (Status: ${response.status})`);
      }

      return { success: true, data: responseText };
    } catch (error) {
      console.error('Error in deleteProfileImage:', error);
      return { success: false, error: error.message };
    }
  },
};

export default userProfileImageService;
